import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { getNewsImageUrl } from '../../utils/formatters';
import {
  NEWS_CATEGORIES,
  NEWS_CATEGORY_MAP,
  NEWS_POST_TYPES,
  NEWS_IMAGE_ACCEPT,
  validateNewsImage,
} from '../news/newsConstants';
import './AdminNews.css';

/** Blank form state for creating a new post. */
const EMPTY_FORM = {
  title: '',
  category: 'other',
  post_type: 'onsite',
  excerpt: '',
  body: '',
  external_url: '',
  is_published: false,
  show_in_banner: false,
};

/**
 * Admin panel for community news. Lists every post (incl. drafts) and provides a
 * create/edit form covering the on-site (markdown) and external-link post types,
 * a cover-image upload, and quick publish/banner toggles. All requests go to the
 * stats-backend ``/api/news`` proxy, which forwards the caller's JWT so the
 * Django API enforces the ``news.manage`` permission.
 *
 * @param {object} props - Component props.
 * @param {object} props.perms - Admin permission helper from useAdminPermissions.
 * @returns {JSX.Element} The news admin panel.
 */
export default function AdminNews({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const canManage = perms?.isOwner || perms?.hasCommand?.('news.manage');

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/news/admin`, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error('load failed');
      setPosts(await r.json());
      setError('');
    } catch {
      setError(t('admin.newsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => {
    if (canManage) loadPosts();
    else setLoading(false);
  }, [canManage, loadPosts]);

  /** Reset the form to create-mode (no post selected). */
  const startNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setExistingImage(null);
    setShowPreview(false);
    setError('');
  };

  /** Populate the form from an existing post for editing. */
  const startEdit = (post) => {
    setEditingId(post.id);
    setForm({
      title: post.title || '',
      category: post.category || 'other',
      post_type: post.post_type || 'onsite',
      excerpt: post.excerpt || '',
      body: post.body || '',
      external_url: post.external_url || '',
      is_published: !!post.is_published,
      show_in_banner: !!post.show_in_banner,
    });
    setImageFile(null);
    setImagePreview(null);
    setExistingImage(post.cover_image_url || null);
    setShowPreview(false);
    setError('');
    // Editing fetches the full post (incl. body) since the list omits it.
    fetch(`${API_URL}/api/news/admin/${post.id}`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((full) => {
        if (full) setForm((f) => ({ ...f, body: full.body || '' }));
      })
      .catch(() => { /* keep list values */ });
  };

  /** Update one form field by name. */
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  /** Validate and stage a chosen cover-image file (with a local preview). */
  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const msg = validateNewsImage(file);
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  /** Upload the staged cover image for ``postId`` (best effort, surfaces errors). */
  const uploadImage = async (postId) => {
    if (!imageFile) return;
    const fd = new FormData();
    fd.append('image', imageFile);
    const r = await fetch(`${API_URL}/api/news/admin/${postId}/image`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: fd,
    });
    if (!r.ok) throw new Error('image upload failed');
  };

  /** Create or update the post, then upload any staged cover image. */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError(t('admin.newsTitleRequired'));
      return;
    }
    if (form.post_type === 'external' && !form.external_url.trim()) {
      setError(t('admin.newsExternalRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const method = editingId ? 'PATCH' : 'POST';
      const url = editingId
        ? `${API_URL}/api/news/admin/${editingId}`
        : `${API_URL}/api/news/admin`;
      const r = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(typeof data?.error === 'string' ? data.error : t('admin.newsSaveFailed'));
      }
      const saved = await r.json();
      if (imageFile) await uploadImage(saved.id);
      await loadPosts();
      startNew();
    } catch (err) {
      setError(err.message || t('admin.newsSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Toggle a boolean field (published/banner) inline from the list. */
  const handleToggle = async (post, field) => {
    try {
      const r = await fetch(`${API_URL}/api/news/admin/${post.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: !post[field] }),
      });
      if (!r.ok) throw new Error('toggle failed');
      await loadPosts();
    } catch {
      setError(t('admin.newsUpdateFailed'));
    }
  };

  /** Delete a post after confirmation. */
  const handleDelete = async (post) => {
    if (!window.confirm(t('admin.newsDeleteConfirm', { title: post.title }))) return;
    try {
      const r = await fetch(`${API_URL}/api/news/admin/${post.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!r.ok && r.status !== 204) throw new Error('delete failed');
      if (editingId === post.id) startNew();
      await loadPosts();
    } catch {
      setError(t('admin.newsDeleteFailed'));
    }
  };

  if (!canManage) {
    return <div className="admin-news"><p className="news-admin-empty">{t('admin.noAccess')}</p></div>;
  }

  const coverPreview = imagePreview || getNewsImageUrl(existingImage);

  return (
    <div className="admin-news">
      <div className="news-admin-header">
        <h2>{t('nav.news')}</h2>
        {editingId && (
          <button type="button" className="news-btn news-btn-ghost" onClick={startNew}>
            + {t('admin.newsNewPost')}
          </button>
        )}
      </div>

      {error && <div className="news-admin-error">{error}</div>}

      <form className="news-admin-form" onSubmit={handleSave}>
        <h3>{editingId ? t('admin.newsEditPost') : t('admin.newsNewPost')}</h3>

        <label className="news-field">
          <span>{t('admin.newsTitleLabel')}</span>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            maxLength={200}
            placeholder={t('admin.newsTitlePlaceholder')}
          />
        </label>

        <div className="news-field-row">
          <label className="news-field">
            <span>{t('admin.category')}</span>
            <select value={form.category} onChange={(e) => setField('category', e.target.value)}>
              {NEWS_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.emoji} {t(c.labelKey)}</option>
              ))}
            </select>
          </label>

          <label className="news-field">
            <span>{t('admin.newsTypeLabel')}</span>
            <select value={form.post_type} onChange={(e) => setField('post_type', e.target.value)}>
              {NEWS_POST_TYPES.map((pt) => (
                <option key={pt.value} value={pt.value}>{t(pt.labelKey)}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="news-field">
          <span>{t('admin.newsExcerptLabel')}</span>
          <textarea
            value={form.excerpt}
            onChange={(e) => setField('excerpt', e.target.value)}
            maxLength={280}
            rows={2}
            placeholder={t('admin.newsExcerptPlaceholder')}
          />
        </label>

        {form.post_type === 'external' ? (
          <label className="news-field">
            <span>{t('admin.newsExternalLabel')}</span>
            <input
              type="url"
              value={form.external_url}
              onChange={(e) => setField('external_url', e.target.value)}
              placeholder="https://..."
            />
          </label>
        ) : (
          <label className="news-field">
            <span>
              {t('admin.newsBodyLabel')}
              <button
                type="button"
                className="news-preview-toggle"
                onClick={() => setShowPreview((v) => !v)}
              >
                {showPreview ? t('admin.edit') : t('admin.newsPreview')}
              </button>
            </span>
            {showPreview ? (
              <div className="news-md-preview">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {form.body || t('admin.newsBodyEmpty')}
                </ReactMarkdown>
              </div>
            ) : (
              <textarea
                value={form.body}
                onChange={(e) => setField('body', e.target.value)}
                rows={10}
                placeholder={t('admin.newsBodyPlaceholder')}
              />
            )}
          </label>
        )}

        <label className="news-field">
          <span>{t('admin.newsCoverLabel')}</span>
          <input type="file" accept={NEWS_IMAGE_ACCEPT} onChange={onPickImage} />
        </label>
        {coverPreview && (
          <img className="news-cover-preview" src={coverPreview} alt={t('admin.newsCoverAlt')} />
        )}

        <div className="news-field-row news-toggles">
          <label className="news-checkbox">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setField('is_published', e.target.checked)}
            />
            <span>{t('admin.newsPublishedCheck')}</span>
          </label>
          <label className="news-checkbox">
            <input
              type="checkbox"
              checked={form.show_in_banner}
              onChange={(e) => setField('show_in_banner', e.target.checked)}
            />
            <span>{t('admin.newsBannerCheck')}</span>
          </label>
        </div>

        <div className="news-form-actions">
          <button type="submit" className="news-btn news-btn-primary" disabled={saving}>
            {saving ? t('settings.saving') : editingId ? t('admin.newsSaveChanges') : t('admin.newsCreate')}
          </button>
          {editingId && (
            <button type="button" className="news-btn news-btn-ghost" onClick={startNew}>
              {t('matchDetail.cancel')}
            </button>
          )}
        </div>
      </form>

      <div className="news-admin-list">
        <h3>{t('admin.newsAllPosts', { count: posts.length })}</h3>
        {loading ? (
          <p className="news-admin-empty">{t('live.loading')}</p>
        ) : posts.length === 0 ? (
          <p className="news-admin-empty">{t('admin.newsEmpty')}</p>
        ) : (
          <table className="news-table">
            <thead>
              <tr>
                <th>{t('admin.newsTitleLabel')}</th>
                <th>{t('admin.category')}</th>
                <th>{t('admin.newsTypeLabel')}</th>
                <th>{t('admin.colStatus')}</th>
                <th>{t('admin.newsColBanner')}</th>
                <th>{t('admin.newsColActions')}</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => {
                const cat = NEWS_CATEGORY_MAP[post.category];
                return (
                  <tr key={post.id} className={editingId === post.id ? 'is-editing' : ''}>
                    <td>{post.title}</td>
                    <td>{cat ? `${cat.emoji} ${t(cat.labelKey)}` : post.category}</td>
                    <td>{post.post_type === 'external' ? t('admin.newsTypeExternal') : t('admin.newsTypeOnsite')}</td>
                    <td>
                      <button
                        type="button"
                        className={`news-pill ${post.is_published ? 'on' : 'off'}`}
                        onClick={() => handleToggle(post, 'is_published')}
                        title={t('admin.newsToggleHint')}
                      >
                        {post.is_published ? t('admin.newsPublished') : t('admin.newsDraft')}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`news-pill ${post.show_in_banner ? 'on' : 'off'}`}
                        onClick={() => handleToggle(post, 'show_in_banner')}
                        title={t('admin.newsToggleHint')}
                      >
                        {post.show_in_banner ? t('admin.newsInBanner') : t('admin.newsNotBanner')}
                      </button>
                    </td>
                    <td className="news-row-actions">
                      <button type="button" className="news-btn news-btn-small" onClick={() => startEdit(post)}>
                        {t('admin.edit')}
                      </button>
                      <button type="button" className="news-btn news-btn-small news-btn-danger" onClick={() => handleDelete(post)}>
                        {t('admin.remove')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
